import { Box, Button, Grid, InputLabel } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { getAnalysisContainer } from '../../container/analysis-module'
import { AnalysisService } from '../../modules/analysis/services/AnalysisService'
import { ANALYSIS_SERVICE_KEY } from '../../modules/analysis'
import { useStyles } from './Immunocom.styles'
import { COLOR_PRIMARY } from '../../routes/color-constants'
import { downloadFile } from '../../common/files/file'
import { FileService } from '../../modules/files/services/FileService'
import { FILE_SERVICE_KEY } from '../../modules/files'
import { getFileContainer } from '../../container/file-module'
import { v4 as uuidv4 } from 'uuid'
import styles from './SecondStep.module.css'
import { AnalysisInmonucompatibility, AnalysisInmonucompatibilityDTO } from 'modules/analysis/models/AnalysisInmonucompatibility'
import { FileType } from './Form'
import { ResultPatientDataDTO } from 'modules/script-executions/models/ResultPatientData'

type SecondStepProps = {
  filesGroup: FileType[]
  clinicID: string
  patients :ResultPatientDataDTO[]
    
  resetSteps: () => void
}

const analysisService = getAnalysisContainer().get<AnalysisService>(ANALYSIS_SERVICE_KEY)
const fileService = getFileContainer().get<FileService>(FILE_SERVICE_KEY)

const stepsLabel = ['initializingAlgorithm', 'downloadResults', 'immunocomError']

export const SecondStep = (props: SecondStepProps) => {
  const { t } = useTranslation()
  const classes = useStyles({ color: COLOR_PRIMARY })
  const [analysis, setAnalysis] = useState<AnalysisInmonucompatibilityDTO>()
  const [success, setSuccess] = useState<boolean | undefined>(undefined)
  const [labels, setLabels] = useState<string[]>([stepsLabel[0]])
  const [errorLog, setErrorLog] = useState<string>('')

useEffect(() => {
  console.log("📁 Archivos recibidos:", props.filesGroup)
  console.log("👥 Pacientes:", props.patients)
  
  let currentIndex = 0
  // errorLog vive en el state de React, que no se refleja en esta clausura
  // (useEffect solo corre una vez, con `[]`); se usa esta variable local
  // para saber, en tiempo real, si algún paciente ya falló.
  let hasError = false
  const subscriptions: any[] = []

  // Crear índice de archivos por IonCode
  const filesByIonCode = props.filesGroup.reduce((acc, file) => {
    if (file.ionCode && file.ionCode !== 'summary') {
      if (!acc[file.ionCode]) {
        acc[file.ionCode] = {}
      }
      if (file.type === 'sequence') acc[file.ionCode].sequence = file
      if (file.type === 'alignment') acc[file.ionCode].alignment = file
    }
    return acc
  }, {} as { [ionCode: string]: { sequence?: FileType, alignment?: FileType, coberture?: FileType } })

  console.log('📋 Archivos organizados por IonCode:', filesByIonCode)

  // Acumula un mensaje de error ya traducido para mostrarlo en el paso 2.
  const appendError = (message: string) => {
    hasError = true
    setErrorLog(prev => (prev ? `${prev}\n${message}` : message))
    setSuccess(false)
  }

  const processNextPatient = () => {
    if (currentIndex >= props.patients.length) {
      if (!hasError) {
        setSuccess(true)
        setLabels(prev => [...prev, stepsLabel[1]])
      }
      return
    }

    const patient = props.patients[currentIndex]
    const barcodeID = patient.barcodeID as string
    
    console.log(`\n🔬 Procesando paciente ${currentIndex + 1}/${props.patients.length}:`)
    console.log(`  Nombre: ${patient.sampleName}`)
    console.log(`  IonCode: ${barcodeID}`)

    // Buscar archivos por IonCode
    const relatedFiles = filesByIonCode[barcodeID]
    
    if (!relatedFiles || !relatedFiles.sequence || !relatedFiles.alignment ) {
      const missing = []
      if (!relatedFiles?.sequence) missing.push('sequence (.fastq)')
      if (!relatedFiles?.alignment) missing.push('alignment (.bam)')
      
      const errorMsg = `Faltan archivos para ${patient.sampleName} (${barcodeID}): ${missing.join(', ')}`
      console.error('❌', errorMsg)
      console.log('  Archivos disponibles:', Object.keys(filesByIonCode))

      setLabels(prev => [...prev, `${stepsLabel[2]} - ${patient.sampleName}`])
      appendError(errorMsg)

      currentIndex++
      processNextPatient()
      return
    }

    console.log('  ✓ Archivos encontrados:')
    console.log('    - Sequence:', relatedFiles.sequence.name)
    console.log('    - Alignment:', relatedFiles.alignment.name)

    const analysisAux: AnalysisInmonucompatibilityDTO = {
      zipFileID: uuidv4(),
      summaryID: props.filesGroup[0].id, // bc_summary siempre es el primero
      sequenceID: relatedFiles.sequence.id,
      alignmentID: relatedFiles.alignment.id,
      cobertureID: "00000000-0000-0000-0000-000000000000",
      clinicID: props.clinicID,
      patients: [patient],
      localTime: Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    console.log('  📤 Enviando análisis...')

    const subscription = analysisService.addInmonucompatibility(analysisAux).subscribe(
      (res) => {
        if (res?.zipFileID) {
          console.log(`  ✅ ${patient.sampleName} completado`)
          setLabels(prev => [...prev, `✓ ${patient.sampleName} completado`])
        } else {
          // La API decodifica la respuesta de error del backend a su clave
          // i18n (p.ej. "errorCreatingTestInmuno", "sampleFailedQualityControl"):
          // se traduce aquí para mostrar un mensaje legible en este paso.
          const errorKey = typeof res === 'string' && res ? res : 'unknownError'
          console.error(`  ❌ ${patient.sampleName} falló:`, errorKey)
          setLabels(prev => [...prev, `✗ ${stepsLabel[2]}: ${patient.sampleName}`])
          appendError(`${patient.sampleName}: ${t(errorKey)}`)
        }

        currentIndex++
        processNextPatient()
      },
      (error) => {
        console.error(`  ❌ Error en ${patient.sampleName}:`, error)
        setLabels(prev => [...prev, `✗ Error: ${patient.sampleName}`])
        appendError(`${patient.sampleName}: ${t('unknownError')}`)

        currentIndex++
        processNextPatient()
      }
    )

    subscriptions.push(subscription)
  }

  processNextPatient()

  return () => {
    subscriptions.forEach(sub => sub.unsubscribe())
  }
}, [])


  return (
    <Box margin={'auto'}>
      <Box margin={'auto'} mb={3} className={styles.box}>
        <ol>
          {labels.map((l) => (
            <li>
              <InputLabel className={styles.label}>{t(l)}</InputLabel>
            </li>
          ))}
        </ol>
      </Box>
      <Grid item xs={12}>
    
        {success === false && (
          <>
          {errorLog && (
            <p style={{fontSize: "15px", marginTop: "3px", marginBottom: "20px", color: "#a11616", whiteSpace: "pre-line"}}>*{errorLog}</p>
          )}
          <Button
            className={classes.button}
            onClick={props.resetSteps}
            variant={'contained'}
            style={{ float: 'none' }}
          >
            {t('backToHome')}
          </Button>
          </>
        )}
      </Grid>
    </Box>
  )
}
